// ============================================
// FAQ 员工留言列表（/faq/questions）
// ============================================

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity, AlertCircle, BookOpen, CheckCircle2, Clock3, HelpCircle,
  LifeBuoy, Loader2, MessageCircle, MessageSquare, Plus, Search,
  UserCircle, UsersRound, XCircle,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import FaqAuthModal from './FaqAuthModal';
import FaqQuestionModal from './FaqQuestionModal';
import { useFaqQuestionStore } from './faq-question-store';
import { tiptapToPlainText } from './faq-rich-content';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqQuestionStatus } from './types';

const statusConfig: Record<FaqQuestionStatus, { label: string; icon: typeof Clock3; className: string }> = {
  open: { label: '待回复', icon: Clock3, className: 'bg-amber-50 text-amber-600 border-amber-100' },
  answered: { label: '已回复', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  closed: { label: '已关闭', icon: XCircle, className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function fmtTime(value: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function FaqQuestionsPage() {
  const navigate = useNavigate();
  const { path } = useFaqSpace();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { questions, loading, error, fetchQuestions } = useFaqQuestionStore();
  const [askOpen, setAskOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<FaqQuestionStatus | 'all'>('all');
  const [scope, setScope] = useState<'all' | 'mine'>(() => searchParams.get('scope') === 'mine' ? 'mine' : 'all');

  useEffect(() => {
    if (user) fetchQuestions();
  }, [user, fetchQuestions]);

  useEffect(() => {
    setScope(searchParams.get('scope') === 'mine' ? 'mine' : 'all');
  }, [searchParams]);

  const scopedQuestions = useMemo(() => {
    if (scope === 'mine') {
      return questions.filter((item) => item.created_by === user?.id);
    }
    return questions;
  }, [questions, scope, user?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scopedQuestions.filter((item) => {
      const statusMatched = status === 'all' || item.status === status;
      const textMatched = !q ||
        item.title.toLowerCase().includes(q) ||
        tiptapToPlainText(item.content).toLowerCase().includes(q) ||
        item.author?.email?.toLowerCase().includes(q) ||
        item.author?.name?.toLowerCase().includes(q);
      return statusMatched && textMatched;
    });
  }, [scopedQuestions, query, status]);

  const counts = useMemo(() => ({
    all: scopedQuestions.length,
    open: scopedQuestions.filter((q) => q.status === 'open').length,
    answered: scopedQuestions.filter((q) => q.status === 'answered').length,
    closed: scopedQuestions.filter((q) => q.status === 'closed').length,
  }), [scopedQuestions]);

  const scopeCounts = useMemo(() => ({
    all: questions.length,
    mine: questions.filter((q) => q.created_by === user?.id).length,
  }), [questions, user?.id]);

  const totalReplies = questions.reduce((sum, item) => sum + (item.reply_count ?? 0), 0);

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
            <MessageSquare size={26} />
          </div>
          <h1 className="text-lg font-bold text-slate-800">留言中心需要登录</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">登录后可以查看内部留言、提交问题并接收管理员回复。</p>
          <button
            onClick={() => setAuthOpen(true)}
            className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600"
          >
            登录 / 注册
          </button>
        </div>
        <FaqAuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800">留言中心</h1>
            <p className="mt-1 text-xs text-slate-400">内部留言、回复跟进和问题沉淀</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={path()}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600"
            >
              <LifeBuoy size={16} />
              支持首页
            </Link>
            <Link
              to={path('knowledge')}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600"
            >
              <BookOpen size={16} />
              知识问答
            </Link>
            <button
              onClick={() => setAskOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600"
            >
              <Plus size={16} />
              我要提问
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[auto_1fr_auto] lg:items-center">
          <div className="flex rounded-xl bg-slate-100 p-1">
            {[
              { key: 'all' as const, label: '全部留言', icon: UsersRound, count: scopeCounts.all },
              { key: 'mine' as const, label: '我的留言', icon: UserCircle, count: scopeCounts.mine },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setScope(tab.key);
                  setStatus('all');
                  setSearchParams(tab.key === 'mine' ? { scope: 'mine' } : {});
                }}
                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition lg:flex-none ${
                  scope === tab.key ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
                <span className={scope === tab.key ? 'text-sky-400' : 'text-slate-400'}>{tab.count}</span>
              </button>
            ))}
          </div>

          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索留言标题、内容或提问人..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
          </div>
          <div className="flex items-center gap-1 overflow-x-auto">
            {[
              { key: 'all' as const, label: '全部', count: counts.all },
              { key: 'open' as const, label: '待回复', count: counts.open },
              { key: 'answered' as const, label: '已回复', count: counts.answered },
              { key: 'closed' as const, label: '已关闭', count: counts.closed },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatus(tab.key)}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  status === tab.key ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {tab.label} {tab.count}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: '全部讨论', value: questions.length, icon: MessageSquare, hint: '员工可见' },
            { label: '待管理员回复', value: questions.filter((q) => q.status === 'open').length, icon: Clock3, hint: '优先处理' },
            { label: '已沉淀回复', value: questions.filter((q) => q.status === 'answered').length, icon: CheckCircle2, hint: '可复用答案' },
            { label: '累计沟通', value: totalReplies, icon: MessageCircle, hint: '补充和回复' },
          ].map((metric) => (
            <div key={metric.label} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-500">{metric.label}</span>
                <metric.icon size={14} className="text-slate-400" />
              </div>
              <div className="mt-2 flex items-end justify-between gap-2">
                <span className="text-2xl font-bold text-slate-800">{metric.value}</span>
                <span className="text-[11px] text-slate-400">{metric.hint}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          <section className="min-w-0">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-bold text-slate-800">
                  {scope === 'mine' ? '我的留言' : '全部讨论'}
                </h2>
                <p className="mt-1 text-xs text-slate-400">按最近活跃排序</p>
              </div>
              <span className="shrink-0 rounded-lg bg-white px-2.5 py-1 text-xs font-semibold text-slate-400">
                {filtered.length} 条
              </span>
            </div>

            {loading && (
              <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-20">
                <Loader2 size={26} className="animate-spin text-sky-500" />
              </div>
            )}

            {error && (
              <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
                <HelpCircle size={42} className="mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-medium text-slate-500">
                  {scope === 'mine' ? '你还没有提交过留言' : '暂无匹配的留言'}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {scope === 'mine' ? '提交后会出现在这里，管理员回复也会同步到详情页' : '可以换个关键词，或先提交一个问题给管理员'}
                </p>
                <button
                  onClick={() => setAskOpen(true)}
                  className="mt-4 rounded-xl bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-600 transition hover:bg-sky-100"
                >
                  提交第一个问题
                </button>
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <div className="space-y-2">
                {filtered.map((item) => {
                  const config = statusConfig[item.status];
                  const Icon = config.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(path(`questions/${item.id}`))}
                      className="group w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition hover:border-sky-200 hover:shadow-sm"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-semibold ${config.className}`}>
                              <Icon size={12} />
                              {config.label}
                            </span>
                            {item.related_faq && (
                              <span className="rounded-lg bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-600">
                                关联 FAQ
                              </span>
                            )}
                            {item.created_by === user.id && (
                              <span className="rounded-lg bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-500">
                                我的留言
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm font-bold text-slate-700 transition group-hover:text-sky-600">{item.title}</h3>
                          <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{tiptapToPlainText(item.content)}</p>
                          {item.related_faq && (
                            <p className="mt-2 line-clamp-1 text-[11px] font-medium text-sky-600">
                              关联知识：{item.related_faq.question}
                            </p>
                          )}
                        </div>
                        <div className="grid shrink-0 grid-cols-3 gap-2 text-center text-xs text-slate-400 lg:w-56">
                          <div className="rounded-xl bg-slate-50 px-2 py-2">
                            <p className="font-bold text-slate-700">{item.reply_count ?? 0}</p>
                            <p className="mt-0.5 text-[10px]">回复</p>
                          </div>
                          <div className="rounded-xl bg-slate-50 px-2 py-2">
                            <p className="font-bold text-slate-700">{item.participant_count ?? 1}</p>
                            <p className="mt-0.5 text-[10px]">参与</p>
                          </div>
                          <div className="rounded-xl bg-slate-50 px-2 py-2">
                            <p className="font-bold text-slate-700">{item.admin_reply_count ?? 0}</p>
                            <p className="mt-0.5 text-[10px]">官方</p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 text-xs text-slate-400">
                        <span className="inline-flex items-center gap-1.5">
                          <UserCircle size={13} />
                          {item.author?.name || item.author?.email || '员工'}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Activity size={13} />
                          最近活跃 {fmtTime(item.last_reply_at || item.updated_at)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>

      <FaqQuestionModal isOpen={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}
